import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Plus, Pencil, Trash2, Mail, AtSign, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SendersPage = () => {
  const { canManageSendersAndTemplates } = useAuth();
  const [senders, setSenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSender, setEditingSender] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchSenders();
  }, []);

  const fetchSenders = async () => {
    try {
      const response = await axios.get(`${API_URL}/senders`);
      setSenders(response.data);
    } catch (error) {
      console.error('Failed to fetch senders:', error);
      toast.error('Failed to load senders');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingSender(null);
    setFormData({ email: '', name: '', description: '' });
    setDialogOpen(true);
  };

  const openEditDialog = (sender) => {
    setEditingSender(sender);
    setFormData({
      email: sender.email,
      name: sender.name,
      description: sender.description || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingSender) {
        await axios.put(`${API_URL}/senders/${editingSender.id}`, formData);
        toast.success('Sender updated successfully');
      } else {
        await axios.post(`${API_URL}/senders`, formData);
        toast.success('Sender created successfully');
      }
      setDialogOpen(false);
      fetchSenders();
    } catch (error) {
      console.error('Failed to save sender:', error);
      toast.error(error.response?.data?.detail || 'Failed to save sender');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (senderId) => {
    if (!window.confirm('Are you sure you want to delete this sender?')) return;

    try {
      await axios.delete(`${API_URL}/senders/${senderId}`);
      toast.success('Sender deleted successfully');
      fetchSenders();
    } catch (error) {
      console.error('Failed to delete sender:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete sender');
    }
  };

  if (!canManageSendersAndTemplates()) {
    return (
      <div className="text-center py-12">
        <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-900">Access Denied</h2>
        <p className="text-slate-500">You don't have permission to manage senders.</p>
      </div>
    );
  }

  return (
    <div data-testid="senders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Sender Management
          </h1>
          <p className="text-slate-500 mt-1">Configure email sender identities for your domain</p>
        </div>
        <Button
          onClick={openCreateDialog}
          className="mt-4 sm:mt-0"
          data-testid="add-sender-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Sender
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50 mb-6">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AtSign className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Domain Verification Required</p>
              <p className="text-sm text-blue-700">
                Ensure your domain is verified in SendGrid before adding sender emails. 
                You can use any email address on your verified domain.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Senders Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-lg" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
              ))}
            </div>
          ) : senders.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900">No senders configured</h3>
              <p className="text-slate-500 mb-4">Add your first sender email to start sending emails.</p>
              <Button onClick={openCreateDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Add Sender
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sender</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {senders.map((sender) => (
                  <TableRow key={sender.id} data-testid={`sender-row-${sender.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <Mail className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{sender.name}</p>
                          <p className="text-sm text-slate-500">{sender.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {sender.description || '-'}
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {new Date(sender.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(sender)}
                          data-testid={`edit-sender-${sender.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(sender.id)}
                          data-testid={`delete-sender-${sender.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingSender ? 'Edit Sender' : 'Add New Sender'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Display Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Sales Team"
                required
                data-testid="sender-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="sales@yourdomain.com"
                required
                data-testid="sender-email-input"
              />
              <p className="text-xs text-slate-500">
                Must be an email on your verified domain
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Used for sales outreach emails"
                rows={3}
                data-testid="sender-description-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-sender-btn">
                {submitting ? 'Saving...' : editingSender ? 'Update Sender' : 'Create Sender'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SendersPage;
